import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  ColorInput,
  Group,
  Loader,
  NumberFormatter,
  Paper,
  Popover,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconInfoCircle, IconPlus, IconX } from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { getFlowStatsExtended, getFlowFeedbackStats } from "../api";
import ModelCostMap from "../Dashboard/ModelCostMap";
import { IconThumbUp, IconThumbDown } from "@tabler/icons-react";
import { BarChart } from "@mantine/charts";
import { useStore } from "../store";
import { estimateCallCostUsd } from "../_Common/modelPricing";
import {
  isDeprecated,
  isUnavailable,
  lifecycleMeta,
  lifecycleOf,
} from "../_Common/modelLifecycle";

const LEVEL_OPTIONS = [
  {
    value: "request",
    label: "Flow Runs",
    description: "End-to-end flow execution (recommended)",
  },
  {
    value: "inference",
    label: "Inference",
    description: "Individual LLM calls",
  },
  {
    value: "tree",
    label: "Agent Trees",
    description: "Including sub-agents",
  },
];

const METRIC_OPTIONS = [
  { label: "Invocations", value: "invocations" },
  { label: "Total Cost", value: "cost" },
  { label: "Avg Cost/Call", value: "avgCost" },
];

function getAnnotationStorageKey(flowName) {
  return `flowAnnotations:${flowName}`;
}

function loadAnnotations(flowName) {
  try {
    const raw = localStorage.getItem(getAnnotationStorageKey(flowName));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveAnnotations(flowName, annotations) {
  localStorage.setItem(
    getAnnotationStorageKey(flowName),
    JSON.stringify(annotations)
  );
}

export default function FlowAnalysis({ flowName }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(7);
  const [level, setLevel] = useState("request");
  const [metric, setMetric] = useState("invocations");
  const [annotations, setAnnotations] = useState(() =>
    loadAnnotations(flowName)
  );

  useEffect(() => {
    setAnnotations(loadAnnotations(flowName));
  }, [flowName]);

  const loadDistributions = period < 90;

  useEffect(() => {
    if (!loadDistributions) {
      setData({ skipDistributions: true, hasAgentTrees: false });
      return;
    }
    setData(null);
    getFlowStatsExtended(flowName, period)
      .then(setData)
      .catch(() =>
        setData({ skipDistributions: true, hasAgentTrees: false })
      );
  }, [flowName, period, loadDistributions]);

  const addAnnotation = useCallback(
    (annotation) => {
      const next = [...annotations, annotation];
      setAnnotations(next);
      saveAnnotations(flowName, next);
    },
    [annotations, flowName]
  );

  const removeAnnotation = useCallback(
    (index) => {
      const next = annotations.filter((_, i) => i !== index);
      setAnnotations(next);
      saveAnnotations(flowName, next);
    },
    [annotations, flowName]
  );

  const availableLevels = LEVEL_OPTIONS.filter(
    (opt) => opt.value !== "tree" || data?.hasAgentTrees
  );

  const timeSeriesData =
    metric === "cost"
      ? data?.costTimeSeries
      : metric === "avgCost"
        ? data?.avgCostTimeSeries
        : data?.timeSeries;

  const timeSeriesColor =
    metric === "invocations" ? "indigo.6" : "green.6";

  const referenceLines = annotations
    .map((a) => ({
      x: new Date(a.date).toISOString(),
      label: a.label,
      color: a.color || "red.6",
    }))
    .filter(Boolean);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="md">
          {period < 90 && (
            <SegmentedControl
              value={level}
              onChange={setLevel}
              data={availableLevels.map((opt) => ({
                value: opt.value,
                label: (
                  <Tooltip label={opt.description} position="bottom">
                    <span>{opt.label}</span>
                  </Tooltip>
                ),
              }))}
            />
          )}
          {!data?.hasAgentTrees && level === "request" && period < 90 && (
            <Text size="xs" c="dimmed">
              No sub-agents detected
            </Text>
          )}
        </Group>
        <SegmentedControl
          value={String(period)}
          data={[
            { label: "24h", value: "1" },
            { label: "7d", value: "7" },
            { label: "30d", value: "30" },
            { label: "90d", value: "90" },
            { label: "180d", value: "180" },
          ]}
          onChange={(v) => setPeriod(Number(v))}
        />
      </Group>

      <ModelCostMap
        periodDays={period}
        flowName={flowName}
        title="Cost by model over time"
      />

      {period >= 90 && (
        <Text size="sm" c="dimmed" mb="md">
          Histograms and percentiles stay on 30 days or less — those scans do
          not survive a long window. The map above is the long-window view.
        </Text>
      )}

      {period < 90 && !data && (
        <Center>
          <Loader />
        </Center>
      )}

      {data && !data.skipDistributions && (
        <>
          <Box mt="lg" p="md">
            <Group justify="space-between" mb="xs">
              <SegmentedControl
                size="xs"
                value={metric}
                onChange={setMetric}
                data={METRIC_OPTIONS}
              />
              <AnnotationButton onAdd={addAnnotation} />
            </Group>
            <BarChart
              h={200}
              data={timeSeriesData}
              dataKey="timestamp"
              series={[{ name: "value", color: timeSeriesColor }]}
              referenceLines={referenceLines}
            />
            {annotations.length > 0 && (
              <Group gap="xs" mt="xs">
                {annotations.map((a, i) => (
                  <Paper
                    key={i}
                    px="xs"
                    py={2}
                    withBorder
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Box
                      w={8}
                      h={8}
                      style={{
                        borderRadius: "50%",
                        backgroundColor:
                          a.color || "var(--mantine-color-red-6)",
                        flexShrink: 0,
                      }}
                    />
                    <Text size="xs">
                      {new Date(a.date).toLocaleDateString()} - {a.label}
                    </Text>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => removeAnnotation(i)}
                    >
                      <IconX size={10} />
                    </ActionIcon>
                  </Paper>
                ))}
              </Group>
            )}
          </Box>

          <FeedbackStatsCard flowName={flowName} period={period} />

          {level === "request" && (
            <RequestLevelStats data={data.requestStats} />
          )}
          {level === "inference" && (
            <InferenceLevelStats data={data.inferenceStats} />
          )}
          {level === "tree" && data.hasAgentTrees && (
            <TreeLevelStats data={data.treeStats} />
          )}
        </>
      )}
    </div>
  );
}

function FeedbackStatsCard({ flowName, period }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getFlowFeedbackStats(flowName, period).then(setStats);
  }, [flowName, period]);

  if (!stats || stats.totalCount === 0) return null;

  const positiveRate =
    stats.totalCount > 0
      ? (
          (stats.byModel.reduce((sum, m) => sum + m.positive, 0) /
            stats.totalCount) *
          100
        ).toFixed(0)
      : 0;

  return (
    <Paper p="sm" withBorder mt="md">
      <Group gap="lg" mb="xs">
        <Group gap="xs">
          <IconThumbUp size={16} color="var(--mantine-color-green-6)" />
          <Text fw={600} fz="lg">
            {positiveRate}%
          </Text>
          <Text size="sm" c="dimmed">
            positive
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {stats.totalCount} ratings
        </Text>
      </Group>
      {stats.byModel.length > 1 && (
        <SimpleGrid cols={stats.byModel.length}>
          {stats.byModel.map((m) => (
            <Stack key={m.model} gap={2}>
              <Text size="xs" fw={500}>
                {m.model}
              </Text>
              <Group gap={4}>
                <IconThumbUp size={12} color="var(--mantine-color-green-6)" />
                <Text size="xs">{m.positive}</Text>
                <IconThumbDown size={12} color="var(--mantine-color-red-6)" />
                <Text size="xs">{m.negative}</Text>
              </Group>
            </Stack>
          ))}
        </SimpleGrid>
      )}
    </Paper>
  );
}

function AnnotationButton({ onAdd }) {
  const [opened, setOpened] = useState(false);
  const [date, setDate] = useState(null);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("red.6");

  const handleAdd = () => {
    if (!date || !label.trim()) return;
    onAdd({ date: date.toISOString(), label: label.trim(), color });
    setDate(null);
    setLabel("");
    setColor("red.6");
    setOpened(false);
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end">
      <Popover.Target>
        <ActionIcon
          size="sm"
          variant="subtle"
          onClick={() => setOpened(!opened)}
        >
          <IconPlus size={14} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs" w={240}>
          <Text size="sm" fw={500}>
            Add Annotation
          </Text>
          <DatePickerInput
            size="xs"
            label="Date"
            value={date}
            onChange={setDate}
            placeholder="Pick date"
          />
          <TextInput
            size="xs"
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            placeholder="e.g. Activated v9"
          />
          <ColorInput
            size="xs"
            label="Color"
            value={color}
            onChange={setColor}
            swatches={[
              "red.6",
              "orange.6",
              "yellow.6",
              "green.6",
              "blue.6",
              "grape.6",
            ]}
          />
          <Button size="xs" onClick={handleAdd} disabled={!date || !label.trim()}>
            Add
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

// Compact percentile table replacing per-model bar charts
function PercentileTable({ rows }) {
  return (
    <Table
      withTableBorder
      withColumnBorders
      fz="xs"
      horizontalSpacing="xs"
      verticalSpacing={4}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Metric</Table.Th>
          <Table.Th ta="right">P50</Table.Th>
          <Table.Th ta="right">P90</Table.Th>
          <Table.Th ta="right">P95</Table.Th>
          <Table.Th ta="right">P99</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => (
          <Table.Tr key={row.label}>
            <Table.Td>{row.label}</Table.Td>
            <Table.Td ta="right">{row.format(row.p50)}</Table.Td>
            <Table.Td ta="right">{row.format(row.p90)}</Table.Td>
            <Table.Td ta="right">{row.format(row.p95)}</Table.Td>
            <Table.Td ta="right">{row.format(row.p99)}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

const fmtMs = (v) =>
  v >= 1000
    ? `${(v / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}s`
    : `${Math.round(v).toLocaleString()}ms`;
const fmtUsd = (v) => `$${v.toFixed(4)}`;
const fmtTokens = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtUsdAdaptive = (v) =>
  v < 1
    ? `$${v.toFixed(4)}`
    : `$${v.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

// Level 2: ShuttleRequest-level statistics
function RequestLevelStats({ data }) {
  if (!data?.byModel?.length) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray">
        No flow run data available for this period.
      </Alert>
    );
  }

  const { summary } = data;

  return (
    <Stack gap="lg">
      {/* Summary */}
      <SimpleGrid cols={4}>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Total Runs
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              thousandSeparator
              decimalScale={0}
              value={summary?.totalRuns || 0}
            />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Avg Runtime
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              thousandSeparator
              decimalScale={0}
              value={summary?.avgMilliseconds || 0}
              suffix=" ms"
            />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Total Cost
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              decimalScale={2}
              value={(summary?.totalCost || 0) / 1000000}
              prefix="$"
            />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Avg Tool Calls
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              decimalScale={1}
              value={summary?.avgFunctionCalls || 0}
            />
          </Text>
        </Paper>
      </SimpleGrid>

      {/* Histograms */}
      <SimpleGrid cols={3}>
        <Box>
          <Text mb="sm" fw={500}>
            Runtime Distribution (ms)
          </Text>
          <BarChart
            h={180}
            data={formatHistogram(data.millisecondsHistogram)}
            dataKey="key"
            series={[{ name: "count", color: "blue.6" }]}
            tickLine="y"
          />
        </Box>
        <Box>
          <Text mb="sm" fw={500}>
            Cost Distribution
          </Text>
          <BarChart
            h={180}
            data={formatHistogram(data.costHistogram, (v) =>
              (v / 1000000).toFixed(4)
            )}
            dataKey="key"
            series={[{ name: "count", color: "green.6" }]}
            tickLine="y"
          />
        </Box>
        <Box>
          <Text mb="sm" fw={500}>
            Tool Calls Distribution
          </Text>
          <BarChart
            h={180}
            data={formatHistogram(data.functionCallsHistogram)}
            dataKey="key"
            series={[{ name: "count", color: "orange.6" }]}
            tickLine="y"
          />
        </Box>
      </SimpleGrid>

      {/* Per-model stats */}
      {data.byModel?.map((modelStats) => (
        <Box key={modelStats.model || "unknown"} mt="md">
          <Text fz="lg" fw={600} mb="sm">
            {modelStats.model || "Unknown Model"}
          </Text>

          <SimpleGrid cols={4} mb="md">
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Runs
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  thousandSeparator
                  decimalScale={0}
                  value={modelStats.count}
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Runtime
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  thousandSeparator
                  decimalScale={0}
                  value={modelStats.avgMilliseconds}
                  suffix=" ms"
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Cost
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={4}
                  value={modelStats.avgCost / 1000000}
                  prefix="$"
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Tool Calls
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={1}
                  value={modelStats.avgFunctionCalls}
                />
              </Text>
            </Paper>
          </SimpleGrid>

          <PercentileTable
            rows={[
              {
                label: "Runtime",
                p50: modelStats.millisecondsP50,
                p90: modelStats.millisecondsP90,
                p95: modelStats.millisecondsP95,
                p99: modelStats.millisecondsP99,
                format: fmtMs,
              },
              {
                label: "Cost",
                p50: modelStats.costP50 / 1000000,
                p90: modelStats.costP90 / 1000000,
                p95: modelStats.costP95 / 1000000,
                p99: modelStats.costP99 / 1000000,
                format: fmtUsd,
              },
            ]}
          />
        </Box>
      ))}
    </Stack>
  );
}

// Level 1: Inference-level statistics (original view)
function InferenceLevelStats({ data }) {
  if (!data?.flowTokenDistributions?.length) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray">
        No inference data available for this period.
      </Alert>
    );
  }

  let invocationsTotal = 0;
  for (const distribution of data.flowTokenDistributions) {
    invocationsTotal += distribution.count;
  }

  return (
    <Stack gap="lg">
      <Text fz="lg">{invocationsTotal} individual inference calls</Text>

      <CostProjection distributions={data.flowTokenDistributions} />

      <SimpleGrid cols={2}>
        <Box>
          <Text mb="sm" fw={500}>
            Input Tokens Distribution
          </Text>
          <BarChart
            h={200}
            data={formatHistogram(data.tokensInHistogram)}
            dataKey="key"
            series={[{ name: "count", color: "violet.6" }]}
            tickLine="y"
          />
        </Box>
        <Box>
          <Text mb="sm" fw={500}>
            Output Tokens Distribution
          </Text>
          <BarChart
            h={200}
            data={formatHistogram(data.tokensOutHistogram)}
            dataKey="key"
            series={[{ name: "count", color: "violet.6" }]}
            tickLine="y"
          />
        </Box>
      </SimpleGrid>

      {data.flowTokenDistributions?.map((distribution) => (
        <Box mt="lg" key={distribution.model}>
          <Text fz="xl" fw={600}>
            {distribution.model}
          </Text>

          <SimpleGrid mb="md" cols={5}>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Invocations
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={0}
                  thousandSeparator
                  value={distribution.count}
                />
              </Text>
            </Paper>

            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Latency
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  thousandSeparator
                  decimalScale={0}
                  value={distribution.avgMilliseconds}
                  suffix=" ms"
                />
              </Text>
            </Paper>

            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Cost
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={3}
                  value={distribution.avgCost * 100}
                  suffix="¢"
                />
              </Text>
            </Paper>

            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Tokens In
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={0}
                  thousandSeparator
                  value={distribution.avgTokensIn}
                />
              </Text>
            </Paper>

            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Tokens Out
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={0}
                  thousandSeparator
                  value={distribution.avgTokensOut}
                />
              </Text>
            </Paper>
          </SimpleGrid>

          <PercentileTable
            rows={[
              {
                label: "Tokens In",
                p50: distribution.p50,
                p90: distribution.p90,
                p95: distribution.p95,
                p99: distribution.p99,
                format: fmtTokens,
              },
              {
                label: "Tokens Out",
                p50: distribution.outP50,
                p90: distribution.outP90,
                p95: distribution.outP95,
                p99: distribution.outP99,
                format: fmtTokens,
              },
            ]}
          />
        </Box>
      ))}
    </Stack>
  );
}

// "What-if" cost comparison. Reprojects the period's observed token volumes
// onto every model's list price to estimate migration cost. We also surface
// how much of each current model's list price was *actually* billed (the
// caching/overhead factor) and can scale projections by that realized factor.
function CostProjection({ distributions }) {
  const providers = useStore((state) => state.providers);
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [filter, setFilter] = useState("");
  const [basis, setBasis] = useState("adjusted");

  if (!providers || !distributions?.length) return null;

  const allModels = providers.flatMap((p) => p.models);
  const byName = new Map(allModels.map((m) => [m.name, m]));

  const totalCalls = distributions.reduce((s, d) => s + d.count, 0);
  const actualTotal = distributions.reduce(
    (s, d) => s + (d.avgCost || 0) * d.count,
    0
  );

  // Per current model: list-price baseline vs what was actually billed.
  // ratio < 1 ⇒ prompt caching saved money; ratio > 1 ⇒ reasoning/tool overhead.
  const current = distributions.map((d) => {
    const desc = byName.get(d.model);
    const baseline = desc
      ? estimateCallCostUsd(desc, d.avgTokensIn, d.avgTokensOut) * d.count
      : null;
    const actual = (d.avgCost || 0) * d.count;
    return {
      model: d.model,
      baseline,
      actual,
      ratio: baseline > 0 ? actual / baseline : null,
    };
  });

  const known = current.filter((c) => c.ratio != null);
  const baselineKnown = known.reduce((s, c) => s + c.baseline, 0);
  const actualKnown = known.reduce((s, c) => s + c.actual, 0);
  const blendedFactor = baselineKnown > 0 ? actualKnown / baselineKnown : null;
  const factor =
    basis === "adjusted" && blendedFactor != null ? blendedFactor : 1;

  const currentModels = new Set(distributions.map((d) => d.model));
  const seen = new Set();
  const rows = allModels
    .filter((m) => {
      if (m.isEmbedding || m.isDecision) return false;
      // You can't migrate to a retired model, so never project onto one
      // unless the flow already runs it. Deprecated models are still usable
      // but stay behind the toggle.
      if (isUnavailable(m) && !currentModels.has(m.name)) return false;
      if (isDeprecated(m) && !includeDeprecated && !currentModels.has(m.name))
        return false;
      if (seen.has(m.name)) return false;
      seen.add(m.name);
      return true;
    })
    .map((m) => {
      const listProjected = distributions.reduce(
        (sum, d) =>
          sum + d.count * estimateCallCostUsd(m, d.avgTokensIn, d.avgTokensOut),
        0
      );
      const projected = listProjected * factor;
      return {
        name: m.name,
        lifecycle: lifecycleOf(m) === "active" ? null : lifecycleMeta(m),
        isCurrent: currentModels.has(m.name),
        projected,
        per1k: totalCalls ? (projected / totalCalls) * 1000 : 0,
        delta: actualTotal > 0 ? (projected - actualTotal) / actualTotal : null,
      };
    })
    .filter((r) => r.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.projected - b.projected);

  const pct = (r) => `${Math.round(r * 100)}%`;

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Text fw={600}>Cost projection — what if this flow ran on…</Text>
        <Checkbox
          size="xs"
          label="Include deprecated"
          checked={includeDeprecated}
          onChange={(e) => setIncludeDeprecated(e.currentTarget.checked)}
        />
      </Group>

      <Group gap="xs" mb="sm">
        <Text size="sm" c="dimmed">
          Actual cost this period:
        </Text>
        <Text size="sm" fw={600}>
          {fmtUsdAdaptive(actualTotal)}
        </Text>
        <Text size="sm" c="dimmed">
          across {totalCalls.toLocaleString()} calls
        </Text>
      </Group>

      {/* Realized cost vs list price — quantifies the caching/overhead benefit */}
      {known.length > 0 && (
        <Paper
          p="sm"
          withBorder
          mb="sm"
          bg="var(--mantine-color-default-hover)"
        >
          <Text size="sm" fw={600} mb={2}>
            Realized cost vs list price
          </Text>
          <Text size="xs" c="dimmed" mb="xs">
            Share of each model's list price you actually paid. Below 100% means
            prompt caching is saving money; above 100% means reasoning or tool
            overhead.
          </Text>
          <Group gap="lg">
            {known.map((c) => (
              <Group key={c.model} gap={6} wrap="nowrap">
                <Text size="sm">{c.model}</Text>
                <Badge
                  size="sm"
                  variant="light"
                  color={
                    c.ratio <= 0.95
                      ? "green"
                      : c.ratio >= 1.05
                        ? "orange"
                        : "gray"
                  }
                >
                  {pct(c.ratio)} of list
                </Badge>
                <Text size="xs" c="dimmed">
                  ({fmtUsdAdaptive(c.actual)} vs {fmtUsdAdaptive(c.baseline)})
                </Text>
              </Group>
            ))}
          </Group>
        </Paper>
      )}

      <Group justify="space-between" mb="xs" align="flex-end">
        <Box>
          <Text size="xs" c="dimmed" mb={2}>
            Cost basis
          </Text>
          <SegmentedControl
            size="xs"
            value={blendedFactor == null ? "list" : basis}
            onChange={setBasis}
            disabled={blendedFactor == null}
            data={[
              { label: "List price", value: "list" },
              {
                label:
                  blendedFactor != null
                    ? `Caching-adjusted (×${blendedFactor.toFixed(2)})`
                    : "Caching-adjusted",
                value: "adjusted",
              },
            ]}
          />
        </Box>
        <TextInput
          size="xs"
          placeholder="Filter models…"
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
          w={240}
        />
      </Group>

      <Text size="xs" c="dimmed" mb="sm">
        {basis === "adjusted" && blendedFactor != null ? (
          <>
            List price scaled by the ×{blendedFactor.toFixed(2)}{" "}
            caching/overhead factor you realized this period. Assumes other
            models cache similarly — a rough cross-model assumption.
          </>
        ) : (
          <>
            Each model's list price applied to this period's token volumes.
            Ignores prompt caching, reasoning tokens and per-use tool costs.
          </>
        )}
      </Text>

      <Table striped highlightOnHover fz="sm" verticalSpacing={4}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Model</Table.Th>
            <Table.Th ta="right">Projected (period)</Table.Th>
            <Table.Th ta="right">Per 1k calls</Table.Th>
            <Table.Th ta="right">Δ vs actual</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.name}>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <span>{r.name}</span>
                  {r.isCurrent && (
                    <Badge size="xs" variant="light" color="blue">
                      current
                    </Badge>
                  )}
                  {r.lifecycle && (
                    <Badge
                      size="xs"
                      variant="light"
                      color={r.lifecycle.color}
                    >
                      {r.lifecycle.label}
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td ta="right">{fmtUsdAdaptive(r.projected)}</Table.Td>
              <Table.Td ta="right">{fmtUsdAdaptive(r.per1k)}</Table.Td>
              <Table.Td ta="right">
                {r.delta == null ? (
                  "—"
                ) : (
                  <Text
                    span
                    size="sm"
                    c={
                      r.delta < 0
                        ? "green.7"
                        : r.delta > 0
                          ? "red.7"
                          : "dimmed"
                    }
                  >
                    {r.delta > 0 ? "+" : ""}
                    {(r.delta * 100).toFixed(0)}%
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

// Level 3: Agent tree statistics
function TreeLevelStats({ data }) {
  if (!data?.summary) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray">
        No agent tree data available for this period.
      </Alert>
    );
  }

  const { summary } = data;

  return (
    <Stack gap="lg">
      {/* Summary */}
      <SimpleGrid cols={5}>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Total Trees
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              thousandSeparator
              decimalScale={0}
              value={summary.totalTrees}
            />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Avg Agents/Tree
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter decimalScale={1} value={summary.avgAgentCount} />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Max Depth
          </Text>
          <Text fw={600} fz="xl">
            {summary.maxDepth}
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Total Tree Cost
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              decimalScale={2}
              value={summary.totalCost / 1000000}
              prefix="$"
            />
          </Text>
        </Paper>
        <Paper p="sm" withBorder>
          <Text c="dimmed" size="sm">
            Avg Tree Runtime
          </Text>
          <Text fw={600} fz="xl">
            <NumberFormatter
              thousandSeparator
              decimalScale={0}
              value={summary.avgMilliseconds}
              suffix=" ms"
            />
          </Text>
        </Paper>
      </SimpleGrid>

      {/* Depth distribution */}
      {data.depthDistribution?.length > 0 && (
        <Box>
          <Text mb="sm" fw={500}>
            Trees by Max Depth
          </Text>
          <BarChart
            h={180}
            data={data.depthDistribution.map((d) => ({
              depth: `Depth ${d.depth}`,
              count: d.count,
              avgCost: d.avgCost / 1000000,
            }))}
            dataKey="depth"
            series={[{ name: "count", color: "grape.6" }]}
            tickLine="y"
          />
        </Box>
      )}

      {/* Agent count distribution */}
      {data.agentCountDistribution?.length > 0 && (
        <Box>
          <Text mb="sm" fw={500}>
            Trees by Agent Count
          </Text>
          <BarChart
            h={180}
            data={data.agentCountDistribution.map((d) => ({
              bucket: `${d.id}${typeof d.id === "number" ? " agents" : ""}`,
              count: d.count,
            }))}
            dataKey="bucket"
            series={[{ name: "count", color: "cyan.6" }]}
            tickLine="y"
          />
        </Box>
      )}

      {/* By root model */}
      {data.byRootModel?.map((modelStats) => (
        <Box key={modelStats.rootModel || "unknown"} mt="md">
          <Text fz="lg" fw={600} mb="sm">
            Orchestrator: {modelStats.rootModel || "Unknown"}
          </Text>

          <SimpleGrid cols={5} mb="md">
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Trees
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  thousandSeparator
                  decimalScale={0}
                  value={modelStats.count}
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Tree Cost
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={4}
                  value={modelStats.avgTreeCost / 1000000}
                  prefix="$"
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Runtime
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  thousandSeparator
                  decimalScale={0}
                  value={modelStats.avgTreeMilliseconds}
                  suffix=" ms"
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Avg Agents
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={1}
                  value={modelStats.avgAgentCount}
                />
              </Text>
            </Paper>
            <Paper p="sm" withBorder>
              <Text c="dimmed" size="xs">
                Total Cost
              </Text>
              <Text fw={600}>
                <NumberFormatter
                  decimalScale={2}
                  value={modelStats.totalCost / 1000000}
                  prefix="$"
                />
              </Text>
            </Paper>
          </SimpleGrid>

          <PercentileTable
            rows={[
              {
                label: "Tree Cost",
                p50: modelStats.costP50 / 1000000,
                p90: modelStats.costP90 / 1000000,
                p95: modelStats.costP95 / 1000000,
                p99: modelStats.costP99 / 1000000,
                format: fmtUsd,
              },
              {
                label: "Tree Runtime",
                p50: modelStats.millisecondsP50,
                p90: modelStats.millisecondsP90,
                p95: modelStats.millisecondsP95,
                p99: modelStats.millisecondsP99,
                format: fmtMs,
              },
            ]}
          />
        </Box>
      ))}
    </Stack>
  );
}

// Helper to format histogram data for charts
function formatHistogram(histogram, formatter = (v) => v.toFixed(0)) {
  if (!histogram?.length) return [];
  return histogram.map((item) => {
    // For integer buckets (width=1), show just the single value
    const isIntBucket =
      Math.abs(item.max - item.min - 1) < 0.001 &&
      Math.abs(item.min - Math.round(item.min)) < 0.001;
    return {
      ...item,
      key: isIntBucket
        ? formatter(item.min)
        : `${formatter(item.min)} - ${formatter(item.max)}`,
    };
  });
}
